import { useState } from "react";
import { OutlineItem } from "@/lib/pdfHeadingExtract";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, GripVertical } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface OutlineEditorProps {
  outline: OutlineItem[];
  numPages: number;
  onSave: (newOutline: OutlineItem[]) => void;
  onCancel: () => void;
}

export const OutlineEditor = ({
  outline,
  numPages,
  onSave,
  onCancel,
}: OutlineEditorProps) => {
  const [items, setItems] = useState<OutlineItem[]>(outline);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        title: "新章節",
        pageNumber: 1,
      },
    ]);
  };

  const handleDeleteItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleUpdateTitle = (index: number, title: string) => {
    const newItems = [...items];
    newItems[index].title = title;
    setItems(newItems);
  };

  const handleUpdatePage = (index: number, pageNumber: number) => {
    const newItems = [...items];
    newItems[index].pageNumber = Math.max(1, Math.min(pageNumber, numPages));
    setItems(newItems);
  };

  const handleSave = () => {
    // 過濾掉標題為空的項目
    const validItems = items.filter(item => item.title.trim().length > 0);
    onSave(validItems);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];

    // 移除舊位置
    newItems.splice(draggedIndex, 1);
    // 插入到新位置
    newItems.splice(index, 0, draggedItem);

    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-sm font-medium mb-2">編輯目錄</h3>

      <ScrollArea className="flex-1">
        <div className="space-y-2 pr-4">
          {items.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              目前沒有章節，點擊下方按鈕新增
            </p>
          ) : (
            items.map((item, index) => (
              <div
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-start gap-2 p-2 rounded-md border bg-card transition-opacity ${
                  draggedIndex === index ? "opacity-50" : "opacity-100"
                }`}
              >
                <div
                  className="cursor-move mt-2"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </div>

                <div className="flex-1 space-y-2">
                  <Input
                    value={item.title}
                    onChange={(e) => handleUpdateTitle(index, e.target.value)}
                    placeholder="章節標題"
                    className="text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">頁碼：</span>
                    <Input
                      type="number"
                      min={1}
                      max={numPages}
                      value={item.pageNumber}
                      onChange={(e) =>
                        handleUpdatePage(index, parseInt(e.target.value) || 1)
                      }
                      className="w-20 text-sm"
                    />
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteItem(index)}
                  className="shrink-0"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>

      </ScrollArea>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button size="sm" variant="outline" onClick={onCancel}>
            取消
          </Button>
          <Button size="sm" onClick={handleSave}>
            保存
          </Button>
        </div>
      <div className="mt-2 space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddItem}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          新增章節
        </Button>
      </div>
    </div>
  );
};
